import { useCallback, useEffect, useRef } from "react";
import { useGetCurrentUserQuery } from "@/shared/services";
import { captureSentryError } from "@/shared/utils/sentry-helpers";
import {
  useAuthStore,
  useAuthStoreShallow,
} from "../store/authentication.store";
import { refreshAuthToken } from "../utils/auth.utils";
import {
  buildRoleSignature,
  decideReconcileAction,
} from "../utils/role-reconcile.utils";

/**
 * Reconciles the live session's Firebase role claim with the DB role.
 *
 * On bootstrap (app load / page refresh): if the DB role and the token role
 * (claims.userType) disagree, force one token refresh to pull the up-to-date
 * claim and push it into the auth store. If they still disagree after the
 * refresh, the claim itself is stale — re-logging in would only mint the same
 * wrong token — so we report to Sentry and keep the user on their token role.
 * We never log the user out.
 */
export const useRoleReconcile = (): void => {
  const { data: currentUser } = useGetCurrentUserQuery();
  const dbRole = currentUser?.type ?? null;

  const { tokenRole, isAdminCore, hasClaims } = useAuthStoreShallow(
    (store) => ({
      tokenRole: store.userType,
      isAdminCore: store.isAdminCore,
      hasClaims: store.claims !== null,
    }),
  );

  // Signature of the last mismatch we force-refreshed but failed to resolve.
  // Prevents re-refreshing / re-reporting the same stuck claim.
  const lastUnresolvedSignatureRef = useRef<string | null>(null);
  const runningRef = useRef(false);

  const runReconcile = useCallback(async () => {
    if (!dbRole || !tokenRole || isAdminCore || !hasClaims) return;
    if (runningRef.current) return;

    const action = decideReconcileAction({
      dbRole,
      tokenRole,
      isAdminCore,
      phase: "initial",
      lastUnresolvedSignature: lastUnresolvedSignatureRef.current,
    });
    if (action !== "refresh") return;

    runningRef.current = true;
    try {
      const result = await refreshAuthToken();
      if (!result) return;

      // onAuthStateChanged does NOT fire on a token refresh, so push the freshly
      // pulled claims into the store ourselves to update the UI's role.
      useAuthStore.getState().setAuthClaims(result.claims);

      const nextIsAdminCore = !!result.claims.admin;
      const nextTokenRole =
        (result.claims.userType as string | undefined) ?? tokenRole;

      const afterAction = decideReconcileAction({
        dbRole,
        tokenRole: nextTokenRole,
        isAdminCore: nextIsAdminCore,
        phase: "afterRefresh",
        lastUnresolvedSignature: lastUnresolvedSignatureRef.current,
      });

      if (afterAction === "report") {
        lastUnresolvedSignatureRef.current = buildRoleSignature(
          dbRole,
          nextTokenRole,
        );
        captureSentryError(
          new Error("Role mismatch unresolved after token refresh"),
          {
            level: "error",
            tags: { area: "auth", reason: "role-claim-mismatch" },
            extra: { dbRole, tokenRole: nextTokenRole },
            fingerprint: ["role-claim-mismatch"],
          },
        );
        return;
      }

      // Resolved by the refresh — clear the guard so a future change retries.
      lastUnresolvedSignatureRef.current = null;
    } catch (error) {
      captureSentryError(
        error instanceof Error ? error : new Error("Role reconcile failed"),
        {
          level: "error",
          tags: { area: "auth", reason: "role-reconcile-error" },
        },
      );
    } finally {
      runningRef.current = false;
    }
  }, [dbRole, tokenRole, isAdminCore, hasClaims]);

  // Reconcile on bootstrap and whenever the DB or token role changes.
  useEffect(() => {
    void runReconcile();
  }, [runReconcile]);
};
